import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import Modal from '../modal'
import Input from '../input'
import TextArea from '../textArea'
import Tabs from '../tabs'

import useModel from '../../common/useModel'
import { safeTranslates } from '../../common/utils'
import required from '../../common/validators/required'
import { connect } from 'react-redux'
import api, { apiAction } from '../../api'

import S from './styled'

const TranslatesModal = ({
  translates = {},
  field = '',
  locales = [],
  defaultLocale,
  onClose,
  onChange,
  textArea,
  withLibrary = false,
  fetchLibrary,
  libraryTexts = [],
  fullWidth,
  optional,
  disabled,
}) => {
  const { text, values, model, onChange: changeModel } = useModel({
    values: translates,
  })

  const selectedElement = useRef(null)

  const [tab, setTab] = useState(translates.libraryID ? 'library' : 'translates')
  const [scrolled, setScrolled ] = useState(false)

  const [searchValue, setSearchValue] = useState('')
  const [libraryFetched, setLibraryFetched] = useState(false)


  useEffect(() => {
    (async () => {
      if (withLibrary && !libraryFetched) {
        await fetchLibrary()
        setLibraryFetched(true)
      }
    })()
  }, [fetchLibrary, withLibrary, libraryFetched])

  useEffect(() => {
    if (withLibrary &&
      translates.libraryID &&
      libraryTexts.length && !scrolled) {
      setTimeout(() => {
        if(selectedElement && selectedElement.current) {
          selectedElement.current.scrollIntoView()
        }
        setScrolled(true)
      }, 10)
    }
  }, [withLibrary, fetchLibrary, translates.libraryID, libraryTexts, scrolled])

  const handleConfirm = useCallback(() => {
    let result = {
      ...values,
    }
    if (tab === 'translates') {
      delete result.libraryID
    } else {
      const translates = libraryTexts.find(lt => lt.id === values.libraryID).translates
      result = {
        libraryID: values.libraryID,
        ...translates,
      }
    }
    onChange(safeTranslates(result))
  }, [libraryTexts, onChange, tab, values])

  const handleSelectSource = useCallback(tab => {
    setTab(tab)
  }, [])

  const handleSearch = useCallback(event => {
    setSearchValue(event.target.value)
    changeModel({
      target: {
        name: 'values.libraryID',
        value: undefined,
      },
    })
  },[changeModel])

  const handleSelectItem = useCallback(item => {
    changeModel({
      target: {
        name: 'values',
        value: {
          libraryID: item.id,
          ...item.translates,
        },
      },
    })
  },[changeModel])

  const searchText = () => ({
    onChange: handleSearch,
    value: searchValue,
  })

  const actions = useMemo(() => {
    return [{
      title: 'Cancel',
      action: onClose,
      blue: true,
      outlined: true,
    }, {
      title: 'Confirm',
      action: handleConfirm,
      blue: true,
      disabled: (
        (tab !== 'translates' && !_.get(model, 'values.libraryID')) ||
        (tab === 'translates' && !model.isValid) || disabled
      ),
    }]
  }, [disabled, handleConfirm, model, onClose, tab])

  const tabs = [
    { label: 'Translates', value: 'translates' },
    withLibrary ?  { label: 'Library Texts', value: 'library' } : null].filter(Boolean)

  const TranslateInput = textArea ? TextArea : Input

  const filteredLibraryTexts = searchValue ? libraryTexts.filter(
    text => {
      const toCheck = text.translates[defaultLocale].toLocaleLowerCase()
      return searchValue.toLocaleLowerCase().split(' ').every(value => toCheck.includes(value))
    },
  ): libraryTexts

  return (
    <Modal isOpened actions={actions} noScroll padding={'10px 20px'} flexRow fullWidth={fullWidth}>
      <Tabs
        selected={tab}
        tabs={tabs}
        onChange={handleSelectSource}
        noMargin
        noPadding
      >
        {
          tab === 'translates' ? (
            <S.Translates>
              {
                locales.map((locale, index) => (
                  <div key={`locale_${index}`}>
                    <TranslateInput {...{
                      name: `values.${locale.id}${field}`,
                      text,
                      title: locale.name,
                      warnEmpty: true,
                      rows: 10,
                      validate: locale.id === defaultLocale ? optional ? null : required : null,
                      error: locale.id === defaultLocale ?
                        _.get(model, `errors.values.${defaultLocale}${field}`) : null,
                      autoFocus: index === 0,
                      disabled,
                    }}
                    />
                  </div>
                ))
              }
            </S.Translates>
          ) : (
            <S.Library>
              <Input {...{
                name: 'search',
                text: searchText,
                title: 'Search',
              }}
              />
              <S.LibraryList>
                {
                  filteredLibraryTexts.map(item => (
                    <S.LibraryItem
                      key={`i_${item.id}`}
                      onClick={disabled ? null : () => handleSelectItem(item)}
                      selected={item.id === _.get(model, 'values.libraryID')}
                      ref={item.id === _.get(model, 'values.libraryID') ? selectedElement : null}
                    >
                      {(item.translates[defaultLocale] || '').split('\n').map((item, key) => {
                        return <span key={key}>{item}<br/></span>
                      })}
                    </S.LibraryItem>
                  ))
                }
              </S.LibraryList>
            </S.Library>
          )
        }
      </Tabs>
    </Modal>
  )
}

const mapStateToProps = (state, { entityName }) => ({
  libraryTexts: state.libraryTexts.list,
  isLoading: state.libraryTexts.isLoading,
})

const mapDispatchToProps = (dispatch, { entityName } ) => ({
  fetchLibrary: () => dispatch(apiAction(api.getLibraryTexts)()),
})

export default connect(mapStateToProps, mapDispatchToProps)(TranslatesModal)
